import { useState } from 'react';
import type { LogoDto } from '@/api/generated/model';

/**
 * The CDN's rungs, smallest first, with the pixel width each one is.
 *
 * Kept in ascending order — `rungFor` relies on it to pick the first rung big
 * enough for what is being asked, rather than the nearest by name.
 */
const RUNGS = [
  { key: 'sm', px: 64 },
  { key: 'md', px: 128 },
  { key: 'lg', px: 256 },
  { key: 'xl', px: 512 },
] as const satisfies readonly { key: keyof LogoDto; px: number }[];

/** The smallest rung that still covers `cssPx` at the given device density. */
function rungFor(logo: LogoDto, cssPx: number, density: number): string {
  const needed = cssPx * density;
  const rung = RUNGS.find((candidate) => candidate.px >= needed);
  // Nothing on the CDN is larger than `xl`, so anything past 512px takes it.
  return logo[rung?.key ?? 'xl'];
}

export interface CrestProps {
  /** Used for the initials fallback. Pass the short name where there is one. */
  name: string;
  logo: LogoDto | null;
  /** Rendered size in CSS pixels. The CDN rung is chosen from this. */
  size: number;
}

/**
 * A crest — a club's or a league's — falling back to its initials.
 *
 * Lives in `shared/` rather than in the clubs module because leagues carry the
 * same `LogoDto` and the transfer-windows screen renders league crests beside
 * club ones. A second copy is how two screens end up disagreeing about which
 * CDN rung to use or what to do when an image 404s.
 *
 * The two cases render as genuinely different things, which is the point:
 *
 *   - **With a crest**, it is a plain transparent image — no circle, no plate,
 *     no background. A club badge is already a finished mark with its own
 *     shape, and dropping it into a filled round avatar both crops the corners
 *     off the wide ones and adds a coloured disc the badge was never drawn to
 *     sit on.
 *   - **Without one**, it is a circular initials avatar, because two letters
 *     floating with no ground behind them read as stray text rather than as a
 *     stand-in for a logo.
 *
 * Two details it handles that a bare `<img>` would not:
 *
 *   - **It picks the right rung.** The CDN serves 64/128/256/512 and `size` is
 *     in CSS pixels, so a 44px crest wants the 64 at 1x and the 128 on a retina
 *     screen. Both go into `srcSet` and the browser chooses — hardcoding one
 *     rung either ships a blurry badge or four times the bytes needed.
 *   - **It survives a dead URL.** `logo` is null for clubs with nothing on file,
 *     and a URL that 404s falls back to the initials rather than leaving a
 *     broken-image icon. The failure is tracked per URL, so a club whose crest
 *     is swapped in later still gets a fresh attempt.
 */
export function Crest({ name, logo, size }: CrestProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  // Resolved together so the retina rung does not need `logo` re-narrowed
  // inside the branch, which is what would otherwise want a cast.
  const sources = logo ? { src: rungFor(logo, size, 1), retina: rungFor(logo, size, 2) } : null;

  if (sources !== null && failedSrc !== sources.src) {
    return (
      <img
        src={sources.src}
        srcSet={`${sources.src} 1x, ${sources.retina} 2x`}
        // Decorative: every crest in the UI sits beside the club's name, so
        // announcing it again is noise for a screen reader.
        alt=""
        width={size}
        height={size}
        loading="lazy"
        onError={() => setFailedSrc(sources.src)}
        style={{
          inlineSize: size,
          blockSize: size,
          // Badges vary wildly in aspect — a round crest, a wide wordmark, a
          // tall shield. `contain` fits each inside the box whole instead of
          // cropping it to a square.
          objectFit: 'contain',
          flex: 'none',
        }}
      />
    );
  }

  return (
    <span
      aria-hidden
      className="flex flex-none items-center justify-center rounded-full bg-muted font-semibold text-foreground"
      style={{ inlineSize: size, blockSize: size, fontSize: Math.max(10, Math.round(size * 0.36)) }}
    >
      {initialsOf(name)}
    </span>
  );
}

/** "Inter Milan" → "IM", "Benfica" → "B". */
function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}
